import {
	Box,
	Button,
	Chip,
	Divider,
	LinearProgress,
	Paper,
	Stack,
	Tooltip,
	Typography,
	Autocomplete,
	TextField,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import PageTitle from "../../components/PageTitle/PageTitle";
import "./Analytics.css";

import { useAuth } from "../../context/authContext";
import { useSite } from "../../context/siteContext";
import NotAuthorized from "../../components/NotAuthorized/NotAuthorized";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AnalyticsCharts from "../../components/AnalyticsCharts/AnalyticsCharts";
import DateRange from "../../components/DateRange/DateRange";
// import FilterBox from "../../components/FilterBox/FilterBox";
import AnalyticsTables from "../../components/AnalyticsTables/AnalyticsTables";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { useTranslation } from "react-i18next";
import { getAllUsers, getMachinesByProvision } from "../../services/analyticsService";
import { getCustomersCusSites } from "../../services/manageCustomerMasterService";
import { getAllCustomerSites } from "../../services/manageSitesMasterService";
import { getUserAllocations } from "../../services/manageSitesService";
import { getRoles } from "../../services/createEditUSerFormService";
import { useOptions } from "../../context/optionContext";
import { resolveEnabledAdminModules } from "../../utilities/adminModules";
import { canAccessOrderAnalytics } from "../../utilities/pageAccess";
// import { DateRangePicker } from "react-date-range";
import { alpha } from "@mui/system";
const getCurrentDate = () => new Date();
const formatDate = (date) => {
	if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	const year = date.getFullYear();
	return `${month}-${day}-${year}`;
};
const formatApiDate = (date) => {
	if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
		return "";
	}

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

// Get the start of the current week (Monday)
const getStartOfWeek = (date) => {
	const day = date.getDay(),
		diff = date.getDate() - day + (day == 0 ? -6 : 1); // adjust when day is sunday
	date.setDate(diff);
	date.setHours(0, 0, 0, 0);
	return date;
};

// Get the end of the current week (Sunday)
const getEndOfWeek = (date) => {
	const startOfWeek = getStartOfWeek(date);
	startOfWeek.setDate(startOfWeek.getDate() + 6);
	startOfWeek.setHours(23, 59, 59, 999);
	return startOfWeek;
};

// Get the start and end of the current month
const getStartOfMonth = (date) => {
	date.setDate(1);
	date.setHours(0, 0, 0, 0);
	return date;
};

const getEndOfMonth = (date) => {
	date.setMonth(date.getMonth() + 1);
	date.setDate(0);
	date.setHours(23, 59, 59, 999);
	return date;
};

// Get the start and end of the current quarter
const getStartOfQuarter = (date) => {
	const currentMonth = date.getMonth();
	let startMonth;
	if (currentMonth <= 2) startMonth = 0; // January to March (Q1)
	else if (currentMonth <= 5) startMonth = 3; // April to June (Q2)
	else if (currentMonth <= 8) startMonth = 6; // July to September (Q3)
	else startMonth = 9; // October to December (Q4)

	date.setMonth(startMonth);
	date.setDate(1);
	date.setHours(0, 0, 0, 0);
	return date;
};

const getEndOfQuarter = (date) => {
	const currentMonth = date.getMonth();
	let endMonth;
	if (currentMonth <= 2) endMonth = 2; // Q1
	else if (currentMonth <= 5) endMonth = 5; // Q2
	else if (currentMonth <= 8) endMonth = 8; // Q3
	else endMonth = 11; // Q4

	date.setMonth(endMonth + 1);
	date.setDate(0);
	date.setHours(23, 59, 59, 999);
	return date;
};

// Get the start and end of the current year
const getStartOfYear = (date) => {
	date.setMonth(0);
	date.setDate(1);
	date.setHours(0, 0, 0, 0);
	return date;
};

const getEndOfYear = (date) => {
	date.setMonth(11);
	date.setDate(31);
	date.setHours(23, 59, 59, 999);
	return date;
};

const Analytics = () => {
	const [error, setError] = useState(null);
	const [customers, setCustomers] = useState([]);
	const [customersOptions, setCustomersOptions] = useState([]);
	const [machines, setMachines] = useState([]);
	const [machinesOptions, setMachinesOptions] = useState([]);
	const [legalEntityOptions, setLegalEntityOptions] = useState([]);
	const [additionalFilters, setAdditionalFilters] = useState([]);
	const [sites, setSites] = useState([]);
	const [sitesOptions, setSitesOptions] = useState([]);
	const [users, setUsers] = useState([]);
	const [usersOptions, setUsersOptions] = useState([]);
	const [roles, setRoles] = useState([]);
	const [rolesOptions, setRolesOptions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [buttonShow, setButtonShow] = useState(false);
	const [selectedRange, setSelectedRange] = useState({
		key: "W",
		label: "Week",
		startDate: formatDate(getStartOfWeek(new Date())),
		endDate: formatDate(getEndOfWeek(new Date())),
	});
	const [selectedDateRange, setSelectedDateRange] = useState([
		getStartOfWeek(new Date()),
		getEndOfWeek(new Date()),
	]);
	const { user, hasPermission } = useAuth();
	const { selectedSite } = useSite();
	const { t } = useTranslation();
	const { optionKeys } = useOptions();
	const enabledAdminModuleSet = useMemo(() => new Set(resolveEnabledAdminModules(optionKeys)), [optionKeys]);
	const ALL_VALUE = t('MENU_ITEMS.ALL');
	const isSuperAdminViewer = Number(user?.user_id) === 1;
	const analyticsTrackingRoleAccess = useMemo(() => {
		if (isSuperAdminViewer) return null;
		const raw = user?.role?.permissions?.analytics?.tracking_role_access || user?.permissions?.analytics?.tracking_role_access;
		return Array.isArray(raw) ? raw.map((roleName) => String(roleName || "").trim()).filter(Boolean) : null;
	}, [isSuperAdminViewer, user]);
	const analyticsTrackingRoleSet = useMemo(
		() => analyticsTrackingRoleAccess ? new Set(analyticsTrackingRoleAccess.map((roleName) => roleName.toLowerCase())) : null,
		[analyticsTrackingRoleAccess]
	);
	const [filters, setFilters] = useState({
		legal_entity: ALL_VALUE,
		customer: ALL_VALUE,
		site: ALL_VALUE,
		user_role: [],
		user: ALL_VALUE,
		region: t('MENU_ITEMS.ALL'),
		machine: ALL_VALUE,
	});

	const buttons = [
		{ key: "T", label: "Today", tooltip: "Today" },
		{ key: "W", label: "Last 7 Days", tooltip: "Week (Last 7 Days)" },
		{ key: "M", label: "Month", tooltip: "Month" },
		{ key: "Q", label: "Quarter", tooltip: "Quarter" },
		{ key: "Y", label: "Year", tooltip: "Year" },
		// { key: "All", label: "All" },
	];


	const [chartQueryFilter, setChartQueryFilter] = useState({});
	const [showCharts, setShowCharts] = useState(true);
	const [selectedMetric, setSelectedMetric] = useState(null);
	const [accessScope, setAccessScope] = useState({ allowAll: false, allowNone: false });
	const [allocatedSiteIds, setAllocatedSiteIds] = useState([]);
	const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);
	const [initialSiteFilterState, setInitialSiteFilterState] = useState(() => ({
		siteNumber: selectedSite || "",
		ready: !selectedSite,
	}));
	const appliedSelectedSiteRef = useRef(false);

	// Autocomplete search input + debounce
	const [customerInput, setCustomerInput] = useState("");
	const [legalEntityInput, setLegalEntityInput] = useState("");
	const [siteInput, setSiteInput] = useState("");
	const [roleInput, setRoleInput] = useState("");
	const [userInput, setUserInput] = useState("");
	const [machineInput, setMachineInput] = useState("");
	const [debouncedCustomerInput, setDebouncedCustomerInput] = useState("");
	const [debouncedLegalEntityInput, setDebouncedLegalEntityInput] = useState("");
	const [debouncedSiteInput, setDebouncedSiteInput] = useState("");
	const [debouncedRoleInput, setDebouncedRoleInput] = useState("");
	const [debouncedUserInput, setDebouncedUserInput] = useState("");
	const [debouncedMachineInput, setDebouncedMachineInput] = useState("");

	useEffect(() => { const t = setTimeout(() => setDebouncedCustomerInput(customerInput), 200); return () => clearTimeout(t); }, [customerInput]);
	useEffect(() => { const t = setTimeout(() => setDebouncedLegalEntityInput(legalEntityInput), 200); return () => clearTimeout(t); }, [legalEntityInput]);
	useEffect(() => { const t = setTimeout(() => setDebouncedSiteInput(siteInput), 200); return () => clearTimeout(t); }, [siteInput]);
	useEffect(() => { const t = setTimeout(() => setDebouncedRoleInput(roleInput), 200); return () => clearTimeout(t); }, [roleInput]);
	useEffect(() => { const t = setTimeout(() => setDebouncedUserInput(userInput), 200); return () => clearTimeout(t); }, [userInput]);
	useEffect(() => { const t = setTimeout(() => setDebouncedMachineInput(machineInput), 200); return () => clearTimeout(t); }, [machineInput]);

	useEffect(() => {
		appliedSelectedSiteRef.current = false;
		if (selectedSite) setLoading(true);
		setInitialSiteFilterState({
			siteNumber: selectedSite || "",
			ready: !selectedSite,
		});
	}, [selectedSite]);

	const normalizeMachineNumber = (value) => {
		if (value == null) return "";
		return String(value).trim().toUpperCase();
	};
	const normalizeEmail = (value) => {
		if (value == null) return "";
		return String(value).trim().toLowerCase();
	};
	const normalizeSiteNumber = (value) => {
		if (value == null) return "";
		return String(value).trim().toUpperCase();
	};
	const getSiteLegalEntity = (site = {}) =>
		String(
			site.site_legal_entity ??
			site.legal_entity ??
			site.siteLegalEntity ??
			site.site_meta?.site_legal_entity ??
			site.meta?.site_legal_entity ??
			""
		).trim();
	const normalizeCustomerNumber = (value) => {
		if (value == null) return "";
		return String(value).trim().toUpperCase();
	};
	const uniqueBy = (list, keyFn) => {
		const map = new Map();
		(list || []).forEach((item) => {
			const key = keyFn(item);
			if (!key || map.has(key)) return;
			map.set(key, item);
		});
		return Array.from(map.values());
	};
	const isAllValue = (value) => value == null || value === ALL_VALUE;
	// user_role is a multi-select filter: an empty array (or an array of only "All")
	// means no role filter is applied.
	const isRoleFilterEmpty = (value) => !Array.isArray(value) || value.length === 0 || value.every(isAllValue);
	const arraysEqualUnordered = (a, b) => {
		const arrA = Array.isArray(a) ? a : [];
		const arrB = Array.isArray(b) ? b : [];
		if (arrA.length !== arrB.length) return false;
		const sortedA = [...arrA].sort();
		const sortedB = [...arrB].sort();
		return sortedA.every((value, index) => value === sortedB[index]);
	};
	const allOption = useMemo(() => {
		const label = t("MENU_ITEMS.ALL");
		return { value: ALL_VALUE, label, searchText: label.toLowerCase() };
	}, [t]);
	const withAllOption = useCallback(
		(options = []) => {
			const filtered = options.filter((opt) => opt?.value !== ALL_VALUE);
			return [allOption, ...filtered];
		},
		[allOption]
	);

	// Efficient filtering for large option lists (debounced, capped)
	const cap = 500;
	const getSearchQuery = (inputValue, selectedOption) => {
		const query = (inputValue || "").toLowerCase();
		if (!query) return "";
		const selectedLabel = (selectedOption?.label || "").toLowerCase();
		return query === selectedLabel ? "" : query;
	};

	const selectedCustomerOption = customersOptions.find(o => o.value === filters.customer);
	const selectedLegalEntityOption = legalEntityOptions.find(o => o.value === filters.legal_entity);
	const selectedSiteOption = sitesOptions.find(o => o.value === filters.site);
	const selectedRoleOptions = Array.isArray(filters.user_role) && filters.user_role.length > 0
		? rolesOptions.filter(o => filters.user_role.includes(o.value))
		: rolesOptions.filter(o => o.value === ALL_VALUE);
	const selectedUserOption = usersOptions.find(o => o.value === filters.user);
	const selectedMachineOption = machinesOptions.find(o => o.value === filters.machine);
	const compactFilterControlSx = {
		flex: {
			xs: "1 1 100%",
			sm: "1 1 calc(50% - 8px)",
			md: "1 1 calc(33.333% - 10px)",
			lg: "1 1 180px",
			xl: "1 1 200px",
		},
		minWidth: { xs: "100%", sm: "calc(50% - 8px)", md: 180, lg: 168, xl: 180 },
		maxWidth: { xs: "100%", sm: "none", lg: 240, xl: 260 },
		"& .MuiOutlinedInput-root": {
			height: { xs: 36, sm: 38, md: 40 },
			bgcolor: "background.paper",
		},
		"& .MuiInputBase-input": {
			fontSize: { xs: 12, sm: 13, md: 14 },
			py: 0.25,
		},
		"& .MuiInputLabel-root": {
			fontSize: { xs: 12, sm: 13, md: 14 },
		},
		"& .MuiAutocomplete-endAdornment": {
			right: { xs: 4, sm: 9 },
		},
	};
	// The role field's custom text summary sits next to the (empty) search
	// input rather than inside it. Without nowrap, once the summary text gets
	// long the row wraps and the leftover empty input drops to its own line,
	// where its blinking caret shows up as a stray "|". Force nowrap + clip
	// so the empty input stays off-screen instead of wrapping below.
	const roleFilterControlSx = {
		...compactFilterControlSx,
		"& .MuiOutlinedInput-root": {
			...compactFilterControlSx["& .MuiOutlinedInput-root"],
			flexWrap: "nowrap",
			overflow: "hidden",
		},
		"& .MuiAutocomplete-input": {
			minWidth: 0,
		},
	};
	// Show the role selection as plain text (like every other filter field)
	// instead of chips, on every screen size: first role truncated to 5 chars,
	// plus "+N" for any additional roles. Full list available on hover.
	const ROLE_TAG_MAX_CHARS = 5;
	const renderRoleSummaryTags = (selected) => {
		if (!selected || selected.length === 0) return null;
		const label = selected[0]?.label || "";
		const truncated = label.length > ROLE_TAG_MAX_CHARS ? `${label.slice(0, ROLE_TAG_MAX_CHARS)}…` : label;
		const extraCount = selected.length - 1;
		const fullTitle = selected.map((option) => option?.label).filter(Boolean).join(", ");
		return (
			<Box
				component="span"
				title={fullTitle}
				sx={{
					ml: "14px",
					display: "inline-flex",
					alignItems: "center",
					minWidth: 0,
					maxWidth: "100%",
					fontSize: "inherit",
					color: "text.primary",
				}}
			>
				<Box component="span" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
					{truncated}
				</Box>
				{extraCount > 0 && (
					<Box component="span" sx={{ flexShrink: 0, ml: "3px" }}>
						+{extraCount}
					</Box>
				)}
			</Box>
		);
	};
	const displayedCustomerOptions = useMemo(() => {
		const base = customersOptions || [];
		const q = getSearchQuery(debouncedCustomerInput, selectedCustomerOption);
		const filtered = q
			? base.filter(o => (o.searchText || o.label || "").includes(q))
			: base;
		return filtered.slice(0, cap);
	}, [customersOptions, debouncedCustomerInput, selectedCustomerOption]);

	const displayedLegalEntityOptions = useMemo(() => {
		const base = legalEntityOptions || [];
		const q = getSearchQuery(debouncedLegalEntityInput, selectedLegalEntityOption);
		const filtered = q
			? base.filter(o => (o.searchText || o.label || "").includes(q))
			: base;
		return filtered.slice(0, cap);
	}, [legalEntityOptions, debouncedLegalEntityInput, selectedLegalEntityOption]);

	const displayedSiteOptions = useMemo(() => {
		const base = sitesOptions || [];
		const q = getSearchQuery(debouncedSiteInput, selectedSiteOption);
		const filtered = q
			? base.filter(o => (o.searchText || o.label || "").includes(q))
			: base;
		return filtered.slice(0, cap);
	}, [sitesOptions, debouncedSiteInput, selectedSiteOption]);

	const displayedRoleOptions = useMemo(() => {
		const base = rolesOptions || [];
		const q = (debouncedRoleInput || "").toLowerCase();
		const filtered = q
			? base.filter(o => (o.searchText || o.label || "").includes(q))
			: base;
		return filtered.slice(0, cap);
	}, [rolesOptions, debouncedRoleInput]);

	const displayedUserOptions = useMemo(() => {
		const base = usersOptions || [];
		const q = getSearchQuery(debouncedUserInput, selectedUserOption);
		const filtered = q
			? base.filter(o => (o.searchText || o.label || "").includes(q))
			: base;
		return filtered.slice(0, cap);
	}, [usersOptions, debouncedUserInput, selectedUserOption]);

	const displayedMachineOptions = useMemo(() => {
		const base = machinesOptions || [];
		const q = getSearchQuery(debouncedMachineInput, selectedMachineOption);
		const filtered = q
			? base.filter(o => (o.searchText || o.label || "").includes(q))
			: base;
		const deduped = [];
		const seen = new Set();
		filtered.forEach((opt) => {
			const key =
				normalizeMachineNumber(opt?.machine_number) ||
				normalizeMachineNumber(opt?.label) ||
				String(opt?.value);
			if (!key || seen.has(key)) return;
			seen.add(key);
			deduped.push(opt);
		});
		return deduped.slice(0, cap);
	}, [machinesOptions, debouncedMachineInput, selectedMachineOption]);

	const isAdminUser = useMemo(() => {
		if (!user) return false;
		if (user.user_id === 1) return true;
		const roleToken =
			user?.role?.role_type ||
			user?.role?.role_name ||
			user?.role?.role ||
			user?.user_role_name ||
			user?.user_role ||
			"";
		return String(roleToken).trim().toLowerCase() === "admin";
	}, [user]);

	useEffect(() => {
		const fetchUsers = async (scope) => {
			try {
				if (scope.allowNone) {
					setUsers([]);
					return;
				}
				const response = await getAllUsers();
				if (response.data.length > 0) {
					const dedupedUsers = uniqueBy(response.data, (u) => normalizeEmail(u?.user_email) || String(u?.user_id));
					setUsers(dedupedUsers);
				} else {
					setError(`Failed to fetch users: No users found`);
				}
			} catch (err) {
				setError(`Failed to fetch users from: ${err.message}`);
			}
		};

		const fetchCustomers = async (scope) => {
			try {
				if (scope.allowNone) {
					setCustomers([]);
					return;
				}
				const response = await getCustomersCusSites();
				if (response.status) {
					const allCustomers = response.data.filter(
						(customer) => customer.customer_name && customer.customer_name !== "EMPTY"
					);
					const dedupedCustomers = uniqueBy(allCustomers, (c) =>
						normalizeCustomerNumber(c?.customer_number) || String(c?.cus_id)
					);
					setCustomers(dedupedCustomers);
				} else {
					setError("Failed to fetch customers.");
				}
			} catch (err) {
				setError("Failed to fetch customers: " + err.message);
			}
		};
		const fetchSites = async (siteIds, scope) => {
			try {
				if (scope.allowNone) {
					setSites([]);
					return;
				}
				const response = await getAllCustomerSites();
				if (response.status) {
					const allSites = response.data.filter(
						(customer) => customer.customer_site_name && customer.customer_site_name !== "EMPTY"
					);
					const dedupedSites = uniqueBy(allSites, (s) =>
						normalizeSiteNumber(s?.customer_site_number || s?.site_number) || String(s?.site_id)
					);
					const filteredSites = scope.allowAll
						? dedupedSites
						: siteIds.length > 0
							? dedupedSites.filter((s) => siteIds.includes(Number(s.site_id)))
							: [];
					setSites(filteredSites);
				} else {
					setError("Failed to fetch customers.");
				}
			} catch (err) {
				setError("Failed to fetch customers: " + err.message);
			}
		};

		const fetchOrders = async () => { };

		const fetchMachines = async (scope) => {
			try {
				if (scope.allowNone) {
					setMachines([]);
					return;
				}
				const response = await getMachinesByProvision();
				if (response.status) {
					const allMachines = (response.data || []).filter(
						(machine) => String(machine?.machine_status || "").toLowerCase() === "active"
					);
					setMachines(allMachines);
				} else {
					setError("Failed to fetch machines.");
				}
			} catch (error) {
				console.error("Error fetching machines:", error);
				setError("Failed to fetch machines.");
			}
		};

		const init = async () => {
			if (!user) return;
			setFilterOptionsLoaded(false);
			setLoading(true);
			try {
				const uid = user.user_id;
				const allocResp = await getUserAllocations(uid);
				const allocations = allocResp?.data?.allocations || [];
				const siteIds = [...new Set(allocations.map((a) => Number(a.site_id)).filter(Boolean))];
				const cusIds = [...new Set(allocations.map((a) => Number(a.cus_id)).filter(Boolean))];
				const hasAllocations = siteIds.length > 0 || cusIds.length > 0;
				const scope = {
					allowAll: isAdminUser && !hasAllocations,
					allowNone: !isAdminUser && !hasAllocations,
				};
				setAccessScope(scope);
				setAllocatedSiteIds(siteIds);
				await fetchSites(siteIds, scope);
				await fetchUsers(scope);
				await fetchCustomers(scope);
				await fetchMachines(scope);
				const rolesResponse = await getRoles();
				setRoles(Array.isArray(rolesResponse?.data) ? rolesResponse.data : []);
			} catch (e) {
				const scope = {
					allowAll: Boolean(isAdminUser),
					allowNone: !isAdminUser,
				};
				setAccessScope(scope);
				setAllocatedSiteIds([]);
				await fetchSites([], scope);
				await fetchUsers(scope);
				await fetchCustomers(scope);
				await fetchMachines(scope);
				const rolesResponse = await getRoles();
				setRoles(Array.isArray(rolesResponse?.data) ? rolesResponse.data : []);
			} finally {
				setFilterOptionsLoaded(true);
			}
		};

		init();
	}, []);

	useEffect(() => {
		const hasFilters =
			!isAllValue(filters.legal_entity) ||
			!isAllValue(filters.customer) ||
			!isAllValue(filters.site) ||
			!isRoleFilterEmpty(filters.user_role) ||
			!isAllValue(filters.user) ||
			!isAllValue(filters.machine);
		setButtonShow(hasFilters);
	}, [filters.legal_entity, filters.customer, filters.site, filters.user_role, filters.user, filters.machine]);

	const appliedDropdownFilters = useMemo(() => {
		const chips = [];
		const legalEntityLabel = legalEntityOptions.find(o => o.value === filters.legal_entity)?.label;
		const customerLabel = customersOptions.find(o => o.value === filters.customer)?.label;
		const siteLabel = sitesOptions.find(o => o.value === filters.site)?.label;
		const roleLabels = Array.isArray(filters.user_role)
			? filters.user_role
				.map((value) => rolesOptions.find(o => o.value === value)?.label)
				.filter(Boolean)
			: [];
		const userLabel = usersOptions.find(o => o.value === filters.user)?.label;
		const machineLabel = machinesOptions.find(o => o.value === filters.machine)?.label;
		if (legalEntityLabel && !isAllValue(filters.legal_entity)) chips.push({ id: "legal_entity", label: legalEntityLabel });
		if (customerLabel && !isAllValue(filters.customer)) chips.push({ id: "customer", label: customerLabel });
		if (siteLabel && !isAllValue(filters.site)) chips.push({ id: "site", label: siteLabel });
		if (roleLabels.length > 0) chips.push({ id: "user_role", label: roleLabels.join(", ") });
		if (userLabel && !isAllValue(filters.user)) chips.push({ id: "user", label: userLabel });
		if (machineLabel && !isAllValue(filters.machine)) chips.push({ id: "machine", label: machineLabel });
		return chips;
	}, [
		filters.legal_entity,
		filters.customer,
		filters.site,
		filters.user_role,
		filters.user,
		filters.machine,
		legalEntityOptions,
		customersOptions,
		sitesOptions,
		rolesOptions,
		usersOptions,
		machinesOptions,
	]);

	// Helper to generate site options
	const getSiteOptions = (sites) =>
		sites.map((site) => ({
			value: site.site_id,
			label: `${site.customer_site_name} - ${site.customer_site_number}`,
			searchText: `${site.customer_site_name} ${site.customer_site_number}`.toLowerCase(),
		}));

	const uniqueMachinesByNumber = (list = []) => {
		const seen = new Map();
		list.forEach((mac) => {
			const key =
				normalizeMachineNumber(mac?.machine_number) ||
				normalizeMachineNumber(mac?.machine_name) ||
				String(mac?.machine_id);
			if (!key || seen.has(key)) return;
			seen.set(key, mac);
		});
		return Array.from(seen.values());
	};

	const getMachineOptions = (machines) =>
		uniqueMachinesByNumber(machines).map((mac) => ({
			key: `${mac.machine_id}_${mac.site_id}_${mac.cus_id}`,
			value: mac.machine_id,
			label: `${mac.machine_name} - ${mac.machine_number}`,
			machine_number: mac.machine_number,
			searchText: `${mac.machine_name} ${mac.machine_number}`.toLowerCase(),
		}));

	const isApprovedUser = (row) => {
		const statusValue = row?.user_status ?? row?.status ?? null;
		if (Number(statusValue) === 1) return true;
		const normalized = String(statusValue || "").trim().toLowerCase();
		return normalized === "approved" || normalized === "active";
	};
	const isAdminRow = (row) => {
		const roleToken =
			row?.user_role_name ||
			row?.user_role ||
			row?.role?.role_name ||
			row?.role?.role_type ||
			row?.user_type ||
			"";
		const normalized = String(roleToken).trim().toLowerCase();
		return normalized === "admin" || normalized.includes("admin");
	};
	const getUserAllocatedSiteIds = (userRow) => {
		if (Array.isArray(userRow?.allocations) && userRow.allocations.length > 0) {
			return Array.from(
				new Set(
					userRow.allocations
						.map((a) => Number(a?.site_id))
						.filter((sid) => Number.isFinite(sid) && sid > 0)
				)
			);
		}
		if (Array.isArray(userRow?.user_sites)) {
			return Array.from(
				new Set(
					userRow.user_sites
						.map((sid) => Number(sid))
						.filter((sid) => Number.isFinite(sid) && sid > 0)
				)
			);
		}
		const rawSites = `${userRow?.user_sites || ""}`.trim();
		if (!rawSites) return [];
		return rawSites
			.split(",")
			.map((sid) => Number(sid))
			.filter((sid) => Number.isFinite(sid) && sid > 0);
	};
	const getAccessibleSiteIdsForUser = (userRow, scopedSites) => {
		const allocatedIds = getUserAllocatedSiteIds(userRow);
		if (allocatedIds.length > 0) return allocatedIds;
		if (isAdminRow(userRow)) return scopedSites.map((site) => Number(site.site_id));
		return [];
	};
	const getCustomerOptions = (customers) =>
		customers.map((cus) => ({
			value: cus.cus_id,
			label: `${cus.customer_name} - ${cus.customer_number}`,
			searchText: `${cus.customer_name} ${cus.customer_number}`.toLowerCase(),
		}));
	const getRoleLabel = (row = {}) =>
		String(
			row?.user_role_name ||
			row?.user_role ||
			row?.role?.role_name ||
			row?.role?.role_type ||
			row?.role_name ||
			row?.role_type ||
			""
		).trim();
	const getRoleOptions = (rolesList = []) =>
		Array.from(
			new Map(
				(rolesList || [])
					.map((role) => String(role?.role_name || role?.role_type || "").trim())
					.filter(Boolean)
					.map((roleName) => [
						roleName.toLowerCase(),
						{
							value: roleName,
							label: roleName,
							searchText: roleName.toLowerCase(),
						},
					])
			).values()
		).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
	const getUserOptions = (usersList) =>
		uniqueBy(
			usersList.map((user) => ({
				value: user.user_id,
				label: `${user.user_display_name} (${user.user_email})`,
				displayName: user.user_display_name,
				email: user.user_email,
				searchText: `${user.user_display_name} ${user.user_email}`.toLowerCase(),
				user_email: user.user_email,
			})),
			(opt) => normalizeEmail(opt?.user_email) || String(opt?.value)
		);

	const baseSites = useMemo(() => {
		if (accessScope.allowNone) return [];
		if (accessScope.allowAll) return sites;
		if (allocatedSiteIds.length > 0) {
			return sites.filter((site) => allocatedSiteIds.includes(Number(site.site_id)));
		}
		return [];
	}, [accessScope.allowAll, accessScope.allowNone, allocatedSiteIds, sites]);

	const baseCustomers = useMemo(() => {
		const customerIds = new Set(
			baseSites
				.map((site) => Number(site?.cus_id))
				.filter((id) => Number.isFinite(id) && id > 0)
		);
		return customers.filter((cus) => customerIds.has(Number(cus.cus_id)));
	}, [baseSites, customers]);

	const areFiltersEqual = (a, b) =>
		a.legal_entity === b.legal_entity &&
		a.customer === b.customer &&
		a.site === b.site &&
		arraysEqualUnordered(a.user_role, b.user_role) &&
		a.user === b.user &&
		a.machine === b.machine;

	const getSitesForMachine = (machineId) => {
		const parsedId = Number(machineId);
		if (!Number.isFinite(parsedId)) return [];
		const siteIds = new Set(
			machines
				.filter((mac) => Number(mac.machine_id) === parsedId)
				.map((mac) => Number(mac.site_id))
				.filter((id) => Number.isFinite(id))
		);
		return baseSites.filter((site) => siteIds.has(Number(site.site_id)));
	};

	const computeScopedSites = (draftFilters) => {
		let scopedSites = baseSites;
		if (!isAllValue(draftFilters.site)) {
			const siteId = Number(draftFilters.site);
			scopedSites = scopedSites.filter((site) => Number(site.site_id) === siteId);
		}
		if (!isAllValue(draftFilters.legal_entity)) {
			scopedSites = scopedSites.filter(
				(site) => getSiteLegalEntity(site) === String(draftFilters.legal_entity).trim()
			);
		}
		if (!isAllValue(draftFilters.customer)) {
			const customerId = Number(draftFilters.customer);
			scopedSites = scopedSites.filter((site) => Number(site.cus_id) === customerId);
		}
		if (!isAllValue(draftFilters.machine)) {
			const sitesForMachine = getSitesForMachine(draftFilters.machine);
			const machineSiteIds = new Set(sitesForMachine.map((site) => Number(site.site_id)));
			scopedSites = scopedSites.filter((site) => machineSiteIds.has(Number(site.site_id)));
		}
		if (!isAllValue(draftFilters.user)) {
			const selectedUser = users.find((user) => Number(user.user_id) === Number(draftFilters.user));
			if (!selectedUser || !isApprovedUser(selectedUser)) {
				return [];
			}
			const userSiteIds = getAccessibleSiteIdsForUser(selectedUser, baseSites);
			const allowedSiteIds = new Set(userSiteIds);
			scopedSites = scopedSites.filter((site) => allowedSiteIds.has(Number(site.site_id)));
		}
		return scopedSites;
	};

	const buildOptionsFromScope = (scopedSites) => {
		const siteIds = new Set(scopedSites.map((site) => Number(site.site_id)));
		const customerIds = new Set(
			scopedSites
				.map((site) => Number(site.cus_id))
				.filter((id) => Number.isFinite(id) && id > 0)
		);
		const scopedCustomers = baseCustomers.filter((cus) => customerIds.has(Number(cus.cus_id)));
		const scopedMachines = machines.filter((mac) => siteIds.has(Number(mac.site_id)));
		const legalEntities = Array.from(
			new Set(scopedSites.map(getSiteLegalEntity).filter(Boolean))
		);
		return {
			legalEntityOptions: legalEntities.map((entity) => ({
				value: entity,
				label: entity,
				searchText: entity.toLowerCase(),
			})),
			customerOptions: getCustomerOptions(scopedCustomers),
			siteOptions: getSiteOptions(scopedSites),
			machineOptions: getMachineOptions(scopedMachines),
			roleOptions: getRoleOptions(
				analyticsTrackingRoleSet
					? roles.filter((role) =>
						analyticsTrackingRoleSet.has(String(role?.role_name || role?.role_type || "").trim().toLowerCase())
					)
					: roles
			),
			userOptions: [],
		};
	};

	const getUserOptionsForScope = (scopedSites, draftFilters) => {
		const approvedUsers = users
			.filter((usr) => Number(usr?.user_id) !== 1)
			.filter(isApprovedUser)
			.filter((usr) =>
				!analyticsTrackingRoleSet ||
				analyticsTrackingRoleSet.has(String(getRoleLabel(usr)).trim().toLowerCase())
			)
			.filter((usr) =>
				isRoleFilterEmpty(draftFilters.user_role) ||
				draftFilters.user_role.map(String).includes(String(getRoleLabel(usr)))
			);
		const adminUsers = approvedUsers.filter((usr) => {
			const allocatedIds = getUserAllocatedSiteIds(usr);
			return allocatedIds.length === 0 && isAdminRow(usr);
		});
		const adminIds = new Set(adminUsers.map((usr) => Number(usr.user_id)));
		const nonAdminUsers = approvedUsers.filter((usr) => !adminIds.has(Number(usr.user_id)));

		const siteIdsForScope = new Set(scopedSites.map((site) => Number(site.site_id)));
		const selectedSiteId = isAllValue(draftFilters.site) ? null : Number(draftFilters.site);
		const selectedCustomerId = isAllValue(draftFilters.customer) ? null : Number(draftFilters.customer);
		const customerSiteIds = selectedCustomerId
			? baseSites
				.filter((site) => Number(site.cus_id) === selectedCustomerId)
				.map((site) => Number(site.site_id))
			: [];

		const requiredSiteIds = selectedSiteId
			? new Set([selectedSiteId])
			: selectedCustomerId
				? new Set(customerSiteIds)
				: siteIdsForScope;

		const scopedUsers = nonAdminUsers.filter((usr) => {
			const userSiteIds = getUserAllocatedSiteIds(usr);
			if (userSiteIds.length === 0) return false;
			return userSiteIds.some((id) => requiredSiteIds.has(Number(id)));
		});

		const shouldIncludeAdmins = requiredSiteIds.size > 0;
		const combined = shouldIncludeAdmins ? [...adminUsers, ...scopedUsers] : scopedUsers;
		return getUserOptions(combined);
	};

	const validateSelections = (draftFilters, options) => {
		const isValid = (value, list) => isAllValue(value) || list.some((opt) => opt.value === value);
		const nextFilters = { ...draftFilters };
		if (!isValid(nextFilters.legal_entity, options.legalEntityOptions)) nextFilters.legal_entity = ALL_VALUE;
		if (!isValid(nextFilters.customer, options.customerOptions)) nextFilters.customer = ALL_VALUE;
		if (!isValid(nextFilters.site, options.siteOptions)) nextFilters.site = ALL_VALUE;
		nextFilters.user_role = Array.isArray(nextFilters.user_role)
			? nextFilters.user_role.filter((value) => options.roleOptions.some((opt) => opt.value === value))
			: [];
		if (!isValid(nextFilters.user, options.userOptions)) nextFilters.user = ALL_VALUE;
		if (!isValid(nextFilters.machine, options.machineOptions)) nextFilters.machine = ALL_VALUE;
		return nextFilters;
	};

	const applyAutoSelection = (draftFilters) => {
		const nextFilters = { ...draftFilters };
		if (!isAllValue(nextFilters.site)) {
			const selectedSite = baseSites.find((site) => Number(site.site_id) === Number(nextFilters.site));
			if (!selectedSite) {
				nextFilters.site = ALL_VALUE;
			} else if (isAllValue(nextFilters.customer)) {
				nextFilters.customer = selectedSite.cus_id ?? nextFilters.customer;
			}
		}
		if (!isAllValue(nextFilters.customer) && !isAllValue(nextFilters.site)) {
			const selectedSite = baseSites.find((site) => Number(site.site_id) === Number(nextFilters.site));
			if (selectedSite && Number(selectedSite.cus_id) !== Number(nextFilters.customer)) {
				nextFilters.site = ALL_VALUE;
			}
		}

		if (!isAllValue(nextFilters.machine)) {
			const candidateSites = getSitesForMachine(nextFilters.machine);
			const candidateSiteIds = new Set(candidateSites.map((site) => Number(site.site_id)));
			if (candidateSites.length === 0) {
				nextFilters.machine = ALL_VALUE;
			} else if (!isAllValue(nextFilters.site)) {
				if (!candidateSiteIds.has(Number(nextFilters.site))) {
					nextFilters.machine = ALL_VALUE;
				}
			} else if (!isAllValue(nextFilters.customer)) {
				const customerCandidates = candidateSites.filter(
					(site) => Number(site.cus_id) === Number(nextFilters.customer)
				);
				if (customerCandidates.length === 1) {
					nextFilters.site = customerCandidates[0].site_id;
					nextFilters.customer = customerCandidates[0].cus_id ?? nextFilters.customer;
				}
			} else if (candidateSites.length === 1) {
				nextFilters.site = candidateSites[0].site_id;
				nextFilters.customer = candidateSites[0].cus_id ?? nextFilters.customer;
			}
		}

		if (!isAllValue(nextFilters.user)) {
			const selectedUser = users.find((user) => Number(user.user_id) === Number(nextFilters.user));
			if (!selectedUser || !isApprovedUser(selectedUser)) {
				nextFilters.user = ALL_VALUE;
			} else if (!isAllValue(nextFilters.site)) {
				const userSiteIds = getAccessibleSiteIdsForUser(selectedUser, baseSites);
				if (!userSiteIds.includes(Number(nextFilters.site))) {
					nextFilters.user = ALL_VALUE;
				}
			}
		}

		return nextFilters;
	};

	const computeNextState = (inputFilters) => {
		let draftFilters = { ...inputFilters };
		for (let i = 0; i < 2; i += 1) {
			draftFilters = applyAutoSelection(draftFilters);
			const scopedSites = computeScopedSites(draftFilters);
			const options = buildOptionsFromScope(scopedSites);
			options.userOptions = getUserOptionsForScope(scopedSites, draftFilters);
			const validatedFilters = validateSelections(draftFilters, options);
			if (areFiltersEqual(draftFilters, validatedFilters)) {
				return { filters: validatedFilters, options };
			}
			draftFilters = validatedFilters;
		}
		const scopedSites = computeScopedSites(draftFilters);
		const options = buildOptionsFromScope(scopedSites);
		options.userOptions = getUserOptionsForScope(scopedSites, draftFilters);
		return { filters: draftFilters, options };
	};

	useEffect(() => {
		if (appliedSelectedSiteRef.current) return;
		if (!selectedSite) {
			appliedSelectedSiteRef.current = true;
			setInitialSiteFilterState({ siteNumber: "", ready: true });
			return;
		}
		if (!filterOptionsLoaded) return;
		const selectedSiteRow = baseSites.find(
			(site) => String(site.customer_site_number) === String(selectedSite)
		);
		appliedSelectedSiteRef.current = true;
		setInitialSiteFilterState({ siteNumber: selectedSite, ready: true });
		if (!selectedSiteRow) return;
		setFilters((prev) => ({
			...prev,
			legal_entity: getSiteLegalEntity(selectedSiteRow) || prev.legal_entity,
			customer: selectedSiteRow.cus_id ?? prev.customer,
			site: selectedSiteRow.site_id,
		}));
	}, [selectedSite, baseSites, filterOptionsLoaded]);

	useEffect(() => {
		const { filters: nextFilters, options } = computeNextState(filters);
		setLegalEntityOptions(withAllOption(options.legalEntityOptions));
		setCustomersOptions(withAllOption(options.customerOptions));
		setSitesOptions(withAllOption(options.siteOptions));
		setRolesOptions(withAllOption(options.roleOptions));
		setUsersOptions(withAllOption(options.userOptions));
		setMachinesOptions(withAllOption(options.machineOptions));
		if (!areFiltersEqual(filters, nextFilters)) {
			setFilters(nextFilters);
		}
	}, [
		baseSites,
		baseCustomers,
		analyticsTrackingRoleSet,
		machines,
		roles,
		users,
		filters,
		withAllOption,
	]);

	// Handle Customer Change
	const handleCustomerChange = (_event, option) => {
		setButtonShow(true);
		const selectedCustomer = option?.value ?? ALL_VALUE;
		setMachineInput("");
		setFilters((prevFilters) => ({
			...prevFilters,
			customer: selectedCustomer,
			site: ALL_VALUE,
			user: ALL_VALUE,
			machine: ALL_VALUE,
		}));
	};

	const handleLegalEntityChange = (_event, option) => {
		setButtonShow(true);
		const selectedLegalEntity = option?.value ?? ALL_VALUE;
		setCustomerInput("");
		setSiteInput("");
		setUserInput("");
		setMachineInput("");
		setFilters((prevFilters) => ({
			...prevFilters,
			legal_entity: selectedLegalEntity,
			customer: ALL_VALUE,
			site: ALL_VALUE,
			user: ALL_VALUE,
			machine: ALL_VALUE,
		}));
	};

	// Handle Site Change
	const handleSiteChange = (_event, option) => {
		setButtonShow(true);
		const selectedSiteId = option?.value ?? ALL_VALUE;
		setMachineInput("");
		setFilters((prevFilters) => ({
			...prevFilters,
			site: selectedSiteId,
		}));
	};

	// Handle User Change
	const handleUserChange = (_event, option) => {
		setButtonShow(true);
		const selectedUserId = option?.value ?? ALL_VALUE;
		setFilters((prevFilters) => ({ ...prevFilters, user: selectedUserId }));
	};

	const handleRoleChange = (_event, options) => {
		setButtonShow(true);
		const selectedRoles = (Array.isArray(options) ? options : [])
			.map((opt) => opt?.value)
			.filter((value) => value !== undefined && value !== null && value !== ALL_VALUE);
		setUserInput("");
		setFilters((prevFilters) => ({ ...prevFilters, user_role: selectedRoles, user: ALL_VALUE }));
	};

	const handleMachineChange = (_event, option) => {
		setButtonShow(true);
		const selectedMachineId = option?.value ?? ALL_VALUE;
		setFilters((prevFilters) => ({
			...prevFilters,
			machine: selectedMachineId,
		}));
	};

	const handleClearFilters = () => {
		setFilters({
			legal_entity: ALL_VALUE,
			customer: ALL_VALUE,
			site: ALL_VALUE,
			user_role: [],
			user: ALL_VALUE,
			region: t('MENU_ITEMS.ALL'),
			machine: ALL_VALUE,
		});

		setLegalEntityInput("");
		setCustomerInput("");
		setSiteInput("");
		setRoleInput("");
		setUserInput("");
		setMachineInput("");
		setButtonShow(false);
	};

	const handleClearDropdownFilter = (filterId) => {
		setFilters((prevFilters) => ({
			...prevFilters,
			[filterId]: filterId === "user_role" ? [] : ALL_VALUE,
			...(filterId === "legal_entity" ? { customer: ALL_VALUE, site: ALL_VALUE, user: ALL_VALUE, machine: ALL_VALUE } : {}),
			...(filterId === "customer" ? { site: ALL_VALUE, user: ALL_VALUE, machine: ALL_VALUE } : {}),
			...(filterId === "user_role" ? { user: ALL_VALUE } : {}),
		}));

		if (filterId === "legal_entity") {
			setLegalEntityInput("");
			setCustomerInput("");
			setSiteInput("");
			setUserInput("");
			setMachineInput("");
		}
		if (filterId === "customer") {
			setCustomerInput("");
			setSiteInput("");
			setUserInput("");
			setMachineInput("");
		}
		if (filterId === "site") setSiteInput("");
		if (filterId === "user_role") {
			setRoleInput("");
			setUserInput("");
		}
		if (filterId === "user") setUserInput("");
		if (filterId === "machine") setMachineInput("");
	};

	const handleClearAllAppliedFilters = () => {
		handleClearFilters();
		setAdditionalFilters([]);
	};

	const normalizeFilterValue = (value) => (isAllValue(value) ? t('MENU_ITEMS.ALL') : value);
	const normalizeRoleFilterValue = (value) => (isRoleFilterEmpty(value) ? t('MENU_ITEMS.ALL') : value);
	const buildChartQuery = () => {
		const siteId = isAllValue(filters.site) ? null : Number(filters.site);
		const selectedSiteRow = siteId ? sites.find((site) => Number(site.site_id) === siteId) : null;
		const siteNumber =
			selectedSiteRow?.customer_site_number ||
			selectedSiteRow?.site_number ||
			null;
		const [rangeStart, rangeEnd] = Array.isArray(selectedDateRange) ? selectedDateRange : [];
		return {
			startDate: formatApiDate(rangeStart) || selectedRange.startDate,
			endDate: formatApiDate(rangeEnd) || selectedRange.endDate,
			mode: selectedRange.key,
			legal_entity: normalizeFilterValue(filters.legal_entity),
			user: normalizeFilterValue(filters.user),
			user_role: normalizeRoleFilterValue(filters.user_role),
			site: normalizeFilterValue(filters.site),
			customer: normalizeFilterValue(filters.customer),
			machine: normalizeFilterValue(filters.machine),
			site_number: siteId ? siteNumber : null,
			additionalFilters: additionalFilters || [],
		};
	};
	const isInitialSiteFilterPending =
		Boolean(selectedSite) &&
		(!initialSiteFilterState.ready || initialSiteFilterState.siteNumber !== selectedSite);
	// Handler for button clicks
	const handleButtonClick = (label, key) => {
		const currentDate = new Date();
		let startDate, endDate;

		switch (key) {
			case "T": // Today
				startDate = new Date(currentDate);
				startDate.setHours(0, 0, 0, 0);

				endDate = new Date(currentDate);
				endDate.setHours(23, 59, 59, 999);
				break;
			case "W": // Week
				startDate = getStartOfWeek(new Date(currentDate));
				endDate = getEndOfWeek(new Date(currentDate));
				break;
			case "Q": // Quarter
				startDate = getStartOfQuarter(new Date(currentDate));
				endDate = getEndOfQuarter(new Date(currentDate));
				break;
			case "M": // Month
				startDate = getStartOfMonth(new Date(currentDate));
				endDate = getEndOfMonth(new Date(currentDate));
				break;
			case "Y": // Year
				startDate = getStartOfYear(new Date(currentDate));
				endDate = getEndOfYear(new Date(currentDate));
				break;

			case t('MENU_ITEMS.ALL'): // Year
				const startOfTwoYearsAgo = new Date(currentDate);
				startOfTwoYearsAgo.setFullYear(currentDate.getFullYear() - 2); // Set start date to 2 years ago
				startDate = startOfTwoYearsAgo;
				endDate = new Date(currentDate);
				break;
			default: // Custom
				startDate = null;
				endDate = null;
				break;
		}

		setSelectedRange({
			label,
			startDate: startDate ? formatDate(startDate) : "",
			endDate: endDate ? formatDate(endDate) : "",
			key,
		});
		setSelectedDateRange([startDate, endDate]);
	};

	const chartQueryRef = useRef("");

	useEffect(() => {
		if (isInitialSiteFilterPending) return;
		const data = buildChartQuery();
		const key = JSON.stringify(data);
		if (key === chartQueryRef.current) return;
		chartQueryRef.current = key;
		setLoading(true);
		setChartQueryFilter(data);
	}, [selectedRange, selectedDateRange, filters, additionalFilters, sites, isInitialSiteFilterPending]);

	const canSeeAnalytics =
		canAccessOrderAnalytics(hasPermission, user) &&
		(user?.user_id === 1 || enabledAdminModuleSet.has("ITEMS_TEXT.ORDER_ANALYTICS"));
	if (!canSeeAnalytics) {
		return <NotAuthorized />;
	}

	return (
		<Box
			sx={{
				width: "100%",
				maxWidth: "100vw",
				display: "flex",
				flexDirection: "column",
				alignItems: "stretch",
				py: 3,
				px: { xs: 2, md: 5 },
				overflowX: "hidden",
			}}>
			<Box
				sx={{
					position: "relative",
					display: "flex",
					alignItems: { xs: "stretch", sm: "center" },
					justifyContent: "center",
					flexDirection: { xs: "column", sm: "row" },
					gap: { xs: 0.75, sm: 1 },
					mb: 0.5,
				}}
			>
				{!showCharts && (
					<Button
						variant="outlined"
						color="primary"
						size="small"
						startIcon={<ArrowBackIcon />}
						onClick={() => setShowCharts(true)}
						sx={{
							display: { xs: "none", xl: "inline-flex" },
							alignSelf: { xs: "flex-start", sm: "center" },
							position: { sm: "absolute" },
							left: { sm: 0 },
							top: { sm: "50%" },
							transform: { sm: "translateY(-50%)" },
							textTransform: "none",
							fontWeight: 800,
							minHeight: 34,
							borderRadius: 1.5,
							px: 1.25,
						}}
					>
						{t("BUTTON.BACK_TO_CHARTS", { defaultValue: "Back to Charts" })}
					</Button>
				)}
				<PageTitle title={t("ITEMS_TEXT.ORDER_ANALYTICS", { defaultValue: "Order Analytics" })} />
			</Box>
			<Paper
				elevation={0}
				sx={{
					px: { xs: 0, md: 0 },
					py: { xs: 1, md: 1.5 },
					mb: 2,
					border: 0,
					borderRadius: 0,
					background: "inherit",
					boxShadow: "none",
					position: { xs: "static", md: "sticky" },
					top: { md: 75 },
					zIndex: 1051,
				}}>
				<Stack
					direction={{ xs: "column", xl: "row" }}
					alignItems={{ xs: "stretch", xl: "center" }}
					justifyContent="space-between"
					spacing={{ xs: 1.5, xl: 1.5 }}
					useFlexGap
					flexWrap="nowrap"
				>
					<Stack
						data-tour="analytics-filters"
						direction="row"
						alignItems="stretch"
						spacing={{ xs: 0.75, sm: 1 }}
						useFlexGap
						flexWrap="wrap"
						sx={{
							flex: { xs: "1 1 100%", xl: "1 1 auto" },
							minWidth: 0,
							width: "100%",
						}}
					>
						<Autocomplete
							size="small"
							disablePortal
							disableClearable={false}
							options={displayedLegalEntityOptions}
							getOptionLabel={(opt) => opt?.label || ''}
							isOptionEqualToValue={(o, v) => o.value === v.value}
							filterOptions={(x) => x}
							onInputChange={(_, val) => setLegalEntityInput(val)}
							inputValue={legalEntityInput}
							value={legalEntityOptions.find(o => o.value === filters.legal_entity) || null}
							onChange={handleLegalEntityChange}
							sx={compactFilterControlSx}
							renderInput={(params) => (
								<TextField
									{...params}
									size="small"
									label="Filter by Legal Entity"
									placeholder="All Legal Entities"
									variant="outlined"
								/>
							)}
						/>
						<Autocomplete
							size="small"
							disablePortal
							disableClearable={false}
							options={displayedCustomerOptions}
							getOptionLabel={(opt) => opt?.label || ''}
							isOptionEqualToValue={(o, v) => o.value === v.value}
							filterOptions={(x) => x}
							onInputChange={(_, val) => setCustomerInput(val)}
							inputValue={customerInput}
							value={customersOptions.find(o => o.value === filters.customer) || null}
							onChange={handleCustomerChange}
							sx={compactFilterControlSx}
							renderInput={(params) => (
								<TextField
									{...params}
									size="small"
									label={t('PLACEHOLDER.FILTER_BY_CUSTOMER')}
									placeholder={t('PLACEHOLDER.FILTER_BY_CUSTOMER')}
									variant="outlined"
								/>
							)}
						/>
						<Autocomplete
							size="small"
							disablePortal
							disableClearable={false}
							disabled={false}
							options={displayedSiteOptions}
							getOptionLabel={(opt) => opt?.label || ''}
							isOptionEqualToValue={(o, v) => o.value === v.value}
							filterOptions={(x) => x}
							onInputChange={(_, val) => setSiteInput(val)}
							inputValue={siteInput}
							value={sitesOptions.find(o => o.value === filters.site) || null}
							onChange={handleSiteChange}
							sx={compactFilterControlSx}
							renderInput={(params) => (
								<TextField
									{...params}
									size="small"
									label={t('PLACEHOLDER.FILTER_BY_SITE')}
									placeholder={t('PLACEHOLDER.FILTER_BY_SITE')}
									variant="outlined"
								/>
							)}
						/>

						<Autocomplete
							size="small"
							disablePortal
							disableClearable={false}
							disabled={false}
							options={displayedMachineOptions}
							getOptionLabel={(opt) => opt?.label || ''}
							isOptionEqualToValue={(o, v) => o.value === v.value}
							filterOptions={(x) => x}
							onInputChange={(_, val) => setMachineInput(val)}
							inputValue={machineInput}
							value={machinesOptions.find(o => o.value === filters.machine) || null}
							onChange={handleMachineChange}
							sx={compactFilterControlSx}
							renderInput={(params) => (
								<TextField
									{...params}
									label={t('PLACEHOLDER.FILTER_BY_MACHINE')}
									placeholder={t('PLACEHOLDER.FILTER_BY_MACHINE')}
									variant="outlined"
									size="small"
								/>
							)}
						/>
						<Autocomplete
							multiple
							disableCloseOnSelect
							size="small"
							disablePortal
							disableClearable={false}
							disabled={false}
							options={displayedRoleOptions}
							getOptionLabel={(opt) => opt?.label || ''}
							isOptionEqualToValue={(o, v) => o.value === v.value}
							filterOptions={(x) => x}
							onInputChange={(_, val) => setRoleInput(val)}
							inputValue={roleInput}
							value={selectedRoleOptions}
							onChange={handleRoleChange}
							renderTags={renderRoleSummaryTags}
							sx={roleFilterControlSx}
							renderInput={(params) => (
								<TextField
									{...params}
									size="small"
									label="Filter by User Role"
									placeholder={selectedRoleOptions.length === 0 ? "All User Roles" : ""}
									variant="outlined"
								/>
							)}
						/>
						<Autocomplete
							size="small"
							disablePortal
							disableClearable={false}
							disabled={false}
							options={displayedUserOptions}
							getOptionLabel={(opt) => opt?.label || ''}
							isOptionEqualToValue={(o, v) => o.value === v.value}
							filterOptions={(x) => x}
							renderOption={(props, option) => (
								<li {...props}>
									<Tooltip title={option?.label || ""} placement="right">
										<Box sx={{ minWidth: 0 }}>
											<Typography
												variant="body1"
												noWrap
												sx={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
												{option?.displayName || option?.label || ""}
											</Typography>
											{option?.email && (
												<Typography
													variant="body2"
													color="text.secondary"
													noWrap
													sx={{ fontSize: "11px", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
													{option.email}
												</Typography>
											)}
										</Box>
									</Tooltip>
								</li>
							)}
							onInputChange={(_, val) => setUserInput(val)}
							inputValue={userInput}
							value={usersOptions.find(o => o.value === filters.user) || null}
							onChange={handleUserChange}
							sx={compactFilterControlSx}
							renderInput={(params) => (
								<TextField
									{...params}
									size="small"
									label={t('PLACEHOLDER.FILTER_BY_USER')}
									placeholder={t('PLACEHOLDER.FILTER_BY_USER')}
									variant="outlined"
								/>
							)}
						/>
					</Stack>
					<Stack
						data-tour="analytics-date-range"
						direction={{ xs: "column", sm: "row" }}
						alignItems="center"
						justifyContent={{ xs: "flex-start", xl: "flex-end" }}
						spacing={{ xs: 0.9, xl: 0.9 }}
						useFlexGap
						flexWrap={{ xs: "wrap", xl: "nowrap" }}
						sx={{ flex: "0 0 auto", width: { xs: "100%", xl: "auto" }, ml: { xl: "auto" } }}
					>
						<Stack
							direction="row"
							alignItems="center"
							spacing={{ xs: 0.5, sm: 0.75 }}
							useFlexGap
							flexWrap={{ xs: "wrap", xl: "nowrap" }}
						>
							{buttons.map((button) => (
								<Tooltip key={button.key} title={button.tooltip} arrow>
									<Button
										onClick={() => handleButtonClick(button.label, button.key)}
										variant="roundedGreyBgSquareContained"
										sx={{
											width: { xs: 28, sm: 35 },
											height: { xs: 28, sm: 35 },
											minWidth: { xs: 28, sm: 35 },
											minHeight: { xs: 28, sm: 35 },
											fontSize: { xs: 12, sm: 14 },
											background: selectedRange.key === button.key ? (theme) => theme.palette.primary.main : (theme) => alpha(theme.palette.primary.light, 0.2),
											color: selectedRange.key === button.key ? "#fff" : (theme) => theme.palette.primary.main
										}}>
										{button.key}
									</Button>
								</Tooltip>
							))}
						</Stack>
						<Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />
						<Box sx={{ width: { xs: "100%", sm: 300, xl: 300 } }}>
							<DateRange
								setSelectedRange={setSelectedRange}
								selectedRange={selectedRange}
								selectedDateRange={selectedDateRange}
								setSelectedDateRange={setSelectedDateRange}
							/>
						</Box>
					</Stack>
				</Stack>
				<Stack
					direction={{ xs: "column", md: "row" }}
					alignItems={{ xs: "flex-start", md: "center" }}
					justifyContent="space-between"
					spacing={2}
					sx={{ mt: 2.5 }}
				>
					<Box sx={{ minWidth: 180 }}>
						<Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{selectedRange.label}</Typography>
						<Typography variant="body1" sx={{ color: "#111827" }}>
							{selectedRange.startDate} - {selectedRange.endDate}
						</Typography>
					</Box>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent={{ xs: "flex-start", md: "flex-end" }}
						spacing={1}
						useFlexGap
						flexWrap="wrap"
						sx={{ minHeight: 32, flex: "1 1 auto" }}
					>
						{(additionalFilters.length > 0 || appliedDropdownFilters.length > 0) && (
							<>
								<Stack direction="row" alignItems="center" spacing={0.5}>
									<FilterAltIcon color="primary" />
									<Typography variant="body2" color="primary" sx={{ fontWeight: 700 }}>
										{t('UI_TEXT.APPLIED_FILTERS')} :{" "}
									</Typography>
								</Stack>
								{appliedDropdownFilters.map((filter) => (
									<Chip
										key={`dropdown-${filter.id}`}
										size="small"
										label={filter.label}
										onDelete={() => handleClearDropdownFilter(filter.id)}
										sx={{
											height: 32,
											borderRadius: 1,
											bgcolor: "#eeeeee",
											color: "#111827",
											fontWeight: 600,
											"& .MuiChip-label": {
												maxWidth: { xs: 220, md: 420 },
												overflow: "hidden",
												textOverflow: "ellipsis",
											},
										}}
									/>
								))}
								{/* {additionalFilters.map((filter) => (
									<Chip
										key={filter.id}
										size="small"
										label={`${filter.chartName || t("UI_TEXT.FILTERED_BY")} - ${filter.name || filter.value || ""}`}
										onDelete={() =>
											setAdditionalFilters(additionalFilters.filter((f) => f.id !== filter.id))
										}
										sx={{
											height: 32,
											borderRadius: 1,
											bgcolor: "#eeeeee",
											color: "#111827",
											fontWeight: 600,
											"& .MuiChip-label": {
												maxWidth: { xs: 220, md: 420 },
												overflow: "hidden",
												textOverflow: "ellipsis",
											},
										}}
									/>
								))} */}
								<Button
									variant="text"
									color="primary"
									size="small"
									startIcon={<FilterAltOffIcon />}
									onClick={handleClearAllAppliedFilters}
									sx={{ fontWeight: 800, textTransform: "none", ml: { xs: 0, md: 1 } }}>
									{t('TITLES.CLEAR_ALL_FILTERS')}
								</Button>
							</>
						)}
					</Stack>
				</Stack>
				<Box sx={{ mt: 1.5, height: 4, borderRadius: 999, overflow: "hidden" }}>
					{loading ? <LinearProgress sx={{ height: 4 }} /> : null}
				</Box>
			</Paper>

			{showCharts ? (
				<AnalyticsCharts
					loading={loading}
					chartFilters={chartQueryFilter}
					setLoading={setLoading}
					selectedRange={selectedRange}
					additionalFilters={additionalFilters}
					setAdditionalFilters={setAdditionalFilters}
					setShowCharts={setShowCharts}
					setSelectedMetric={setSelectedMetric}
					selectedMetric={selectedMetric}
				/>
			) : (
				<AnalyticsTables
					loading={loading}
					chartFilters={chartQueryFilter}
					setLoading={setLoading}
					selectedRange={selectedRange}
					additionalFilters={additionalFilters}
					setAdditionalFilters={setAdditionalFilters}
					setShowCharts={setShowCharts}
					setSelectedMetric={setSelectedMetric}
					selectedMetric={selectedMetric}
				/>
			)}
			{/* <FilterBox additionalFilters={additionalFilters} setAdditionalFilters={setAdditionalFilters} /> */}
		</Box>
	);
};

export default Analytics;
